import {reactive, computed} from 'vue'
import ApiService from '@/core/services/ApiService'
import {endpoints} from '@/core/services/Endpoints'
import panelManager from '@/core/helpers/panelManager'
import platformHandler from '@/core/context/platformHandler'

const NS = 'fileManagerPanel'

const CATEGORIES = {
    ALL: 'ALL',
    MODULE_NOTE: 'MODULE_NOTE',
    MODULE_VEHICLE_WORK_ORDER: 'MODULE_VEHICLE_WORK_ORDER',
    MODULE_MUSIC: 'MODULE_MUSIC',
    MODULE_MUSIC_PLAYLIST: 'MODULE_MUSIC_PLAYLIST',
    UNATTACHED: 'UNATTACHED',
    TRASH: 'TRASH',
}

const CATEGORY_LABELS = {
    ALL: 'common.fileManager.allFiles',
    MODULE_NOTE: 'common.fileManager.noteAttachments',
    MODULE_VEHICLE_WORK_ORDER: 'common.fileManager.workOrderAttachments',
    MODULE_MUSIC: 'common.fileManager.musicFiles',
    MODULE_MUSIC_PLAYLIST: 'common.fileManager.playlistFiles',
    UNATTACHED: 'common.fileManager.independentFiles',
    TRASH: 'common.fileManager.trash',
}

const CATEGORY_ICONS = {
    ALL: 'all',
    MODULE_NOTE: 'note',
    MODULE_VEHICLE_WORK_ORDER: 'workorder',
    MODULE_MUSIC: 'music',
    MODULE_MUSIC_PLAYLIST: 'playlist',
    UNATTACHED: 'unattached',
    TRASH: 'trash',
}

const MODULE_ICON_SVGS = {
    MODULE_NOTE: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>',
    MODULE_VEHICLE_WORK_ORDER: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18.4 7.6c-.3-.8-1.1-1.6-2-1.6H7.6c-.9 0-1.7.8-2 1.6L3.5 11.1C2.7 11.3 2 12.1 2 13v3c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/><path d="M9 17h6"/></svg>',
    MODULE_MUSIC: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
    MODULE_MUSIC_PLAYLIST: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
}

const MODULE_COLORS = {
    MODULE_NOTE: '#F59E0B',
    MODULE_VEHICLE_WORK_ORDER: '#0EA5E9',
    MODULE_MUSIC: '#8B5CF6',
    MODULE_MUSIC_PLAYLIST: '#A78BFA',
}

const ENTITY_NAV = {
    MODULE_NOTE: {action: 'panel', panelId: 'noteApp', paramKey: 'itemId', labelKey: 'common.fileManager.noteLabel'},
    MODULE_VEHICLE_WORK_ORDER: {action: 'route', path: '/module/vehicle-work-order/save/', labelKey: 'common.fileManager.workOrderLabel'},
    MODULE_MUSIC: {action: 'panel', panelId: 'musicPanel', paramKey: 'trackId', labelKey: 'common.fileManager.musicLabel'},
    MODULE_MUSIC_PLAYLIST: {action: 'panel', panelId: 'musicPanel', paramKey: 'playlistId', labelKey: 'common.fileManager.playlistLabel'},
}

function filterEntities(entities, fmConfig) {
    const exclude = fmConfig.excludeEntities
    const map = fmConfig.entityModuleMap
    if (!map && !exclude) return entities
    const codes = platformHandler.getModuleCodes()
    return entities.filter(c => {
        if (exclude && exclude.includes(c.type)) return false
        const moduleCode = map?.[c.type]
        if (!moduleCode) return true
        return codes.includes(moduleCode)
    })
}

const MAX_FOLDER_DEPTH = 5

function getFolderDepth(folderId) {
    let depth = 0
    let id = folderId
    while (id) {
        const f = state.folders.find(fl => fl.id === id)
        if (!f || !f.parentId) break
        depth++
        id = f.parentId
    }
    return depth
}

function canCreateSubfolder(parentId) {
    if (!parentId) return true
    return getFolderDepth(parentId) + 1 < MAX_FOLDER_DEPTH
}

function isDescendantOf(folderId, ancestorId) {
    let id = folderId
    while (id) {
        const f = state.folders.find(fl => fl.id === id)
        if (!f || !f.parentId) return false
        if (f.parentId === ancestorId) return true
        id = f.parentId
    }
    return false
}

function resolveEntityLabel(type, id, $t) {
    const nav = ENTITY_NAV[type]
    if (!nav) return type || ''
    const label = $t ? $t(nav.labelKey) : nav.labelKey
    return label + (id ? ' #' + id : '')
}

function navigateEntity(type, id, router) {
    const nav = ENTITY_NAV[type]
    if (!nav || !id) return
    if (nav.action === 'panel') {
        panelManager.openPanel(nav.panelId, {[nav.paramKey]: id})
    } else if (nav.action === 'route' && router) {
        router.push(nav.path + id)
    }
}

const FILE_ICONS = {
    image: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>',
    pdf: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M10 12h4"/><path d="M10 16h4"/></svg>',
    document: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>',
    spreadsheet: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/><line x1="12" y1="9" x2="12" y2="21"/></svg>',
    archive: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 8v13H3V8"/><path d="M1 3h22v5H1z"/><path d="M10 12h4"/></svg>',
    video: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m10 9 5 3-5 3z"/></svg>',
    audio: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
    generic: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>',
    folder: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>',
}

function getFileIcon(extension) {
    const ext = (extension || '').toLowerCase()
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) return 'image'
    if (ext === 'pdf') return 'pdf'
    if (['doc', 'docx', 'txt', 'rtf', 'odt', 'pages'].includes(ext)) return 'document'
    if (['xls', 'xlsx', 'csv', 'ods', 'numbers'].includes(ext)) return 'spreadsheet'
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'archive'
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return 'video'
    if (['mp3', 'wav', 'ogg', 'flac', 'aac'].includes(ext)) return 'audio'
    return 'generic'
}

function getFileIconSvg(extension) {
    return FILE_ICONS[getFileIcon(extension)] || FILE_ICONS.generic
}

function formatFileSize(bytes) {
    if (!bytes || bytes === 0) return '0 B'
    const units = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(1)) + ' ' + units[i]
}

function formatDate(dateStr) {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    const day = d.getDate().toString().padStart(2, '0')
    const month = (d.getMonth() + 1).toString().padStart(2, '0')
    const year = d.getFullYear()
    const hour = d.getHours().toString().padStart(2, '0')
    const min = d.getMinutes().toString().padStart(2, '0')
    return `${day}.${month}.${year} ${hour}:${min}`
}

const state = reactive({
    files: [],
    folders: [],
    currentFolderId: null,
    activeCategory: CATEGORIES.ALL,
    searchQuery: '',
    viewMode: 'grid',
    autoPreview: true,
    sortBy: 'name',
    sortDir: 'asc',
    selectedIds: [],
    previewFile: null,
    status: 'idle',
    loading: false,
    _initialized: false,
    uploadQueue: [],
    storageQuota: null,
    storageBreakdown: [],
    dockBadge: true,

    apiCategories: [],
    filePage: 1,
    fileHasMore: false,
    fileLoading: false,
    fileTotalCount: null,
    _pageLimit: 30,

    entityPage: 1,
    entityHasMore: false,
    entityLoading: false,
    entityParentId: null,

    searchLoading: false,

    _unattachedCount: 0,
    _trashCount: 0,
    trashFolders: [],
    trashFolderId: null,
    trashCurrentFolder: null,
    lastError: null,
    _draggingSidebarFolderId: null,
    _pickerEntity: null,
    _categoriesReady: false,
    _foldersReady: false,
    _pickerEvents: null,

    navHistory: [],
    navHistoryIndex: -1,
})

let _navFromHistory = false

function pushHistory() {
    if (_navFromHistory) return
    const entry = {
        category: state.activeCategory,
        folderId: state.currentFolderId,
        trashFolderId: state.trashFolderId,
    }
    const last = state.navHistory[state.navHistoryIndex]
    if (last && last.category === entry.category && last.folderId === entry.folderId && last.trashFolderId === entry.trashFolderId) return
    state.navHistory.splice(state.navHistoryIndex + 1)
    state.navHistory.push(entry)
    state.navHistoryIndex = state.navHistory.length - 1
}

async function _restoreHistoryEntry(entry) {
    _navFromHistory = true
    if (entry.category === CATEGORIES.TRASH) {
        state.activeCategory = CATEGORIES.TRASH
        state.currentFolderId = null
        await navigateTrashFolder(entry.trashFolderId)
    } else {
        state.activeCategory = entry.category
        await navigateToFolder(entry.folderId)
    }
    _navFromHistory = false
}

async function goBack() {
    if (state.navHistoryIndex <= 0) return
    state.navHistoryIndex--
    await _restoreHistoryEntry(state.navHistory[state.navHistoryIndex])
}

async function goForward() {
    if (state.navHistoryIndex >= state.navHistory.length - 1) return
    state.navHistoryIndex++
    await _restoreHistoryEntry(state.navHistory[state.navHistoryIndex])
}

const canGoBack = computed(() => state.navHistoryIndex > 0)
const canGoForward = computed(() => state.navHistoryIndex < state.navHistory.length - 1)

function generateId() {
    return 'f_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 7)
}

function mapApiFile(f) {
    const ext = f.extension || (f.name || '').split('.').pop() || ''
    return {
        id: f.id,
        uid: 'file_' + f.id,
        name: f.name || ('file_' + f.id),
        type: 'file',
        mimeType: null,
        size: f.size || 0,
        sizeText: f.size_text || null,
        extension: ext,
        thumbnailUrl: f.thumb?.small || null,
        publicUrl: f.public_url || '#',
        folderId: null,
        module: null,
        moduleEntityId: null,
        createdAt: f.created_at || null,
        updatedAt: f.updated_at || null,
        relationsCount: f.relations_count || 0,
        meta: f.meta || null,
    }
}

function mapApiFolderTree(nodes, parentId) {
    const result = []
    for (const n of nodes) {
        result.push({
            id: n.id,
            uid: 'folder_' + n.id,
            name: n.title || n.name || ('Klasör #' + n.id),
            type: 'folder',
            parentId: parentId,
            fileCount: n.uploads_count || 0,
            childrenCount: n.children?.length || 0,
            color: n.color || '#6B7280',
            sort: n.sort ?? 0,
            createdAt: n.created_at || null,
            children: n.children || [],
        })
        if (n.children && n.children.length) {
            result.push(...mapApiFolderTree(n.children, n.id))
        }
    }
    return result
}

async function apiInit() {
    const fmConfig = platformHandler.getFileManagerConfig()
    try {
        let moduleFolders = []
        let apiFolders = []

        const entitiesPromise = fmConfig.entities
            ? ApiService.axios.get(endpoints.file_manager_entities).then(res => {
                const entities = res.data?.data || res.data || []
                const all = Array.isArray(entities) ? entities : []
                state.apiCategories = filterEntities(all, fmConfig)
                const sidebarCats = state.apiCategories.filter(c => c.type !== 'FILE_MANAGER_FOLDER')
                moduleFolders = sidebarCats.map(c => ({
                    id: 'module_' + c.type,
                    uid: 'folder_module_' + c.type,
                    name: c.label || c.type,
                    nameKey: CATEGORY_LABELS[c.type] || null,
                    type: 'folder',
                    parentId: null,
                    fileCount: c.file_count || 0,
                    color: MODULE_COLORS[c.type] || '#6B7280',
                    createdAt: null,
                    moduleType: c.type,
                    moduleIcon: MODULE_ICON_SVGS[c.type] || null,
                    directUpload: c.direct_upload || false,
                }))
                state._categoriesReady = true
            })
            : Promise.resolve().then(() => { state._categoriesReady = true })

        const foldersPromise = ApiService.axios.get(endpoints.file_manager_folder_tree).then(res => {
            const folderData = res.data?.data || res.data || []
            apiFolders = Array.isArray(folderData) ? mapApiFolderTree(folderData, null) : []
            state._foldersReady = true
        })

        await Promise.all([entitiesPromise, foldersPromise])

        state.folders = [...moduleFolders, ...apiFolders]

        if (state._pickerEntity) {
            const exists = state.folders.some(f => f.id === state._pickerEntity.id)
            if (!exists) state.folders.push(state._pickerEntity)
        }

        await loadFiles(1)

        return true
    } catch {
        state._categoriesReady = true
        state._foldersReady = true
        return false
    }
}

async function loadStorageQuota() {
    if (!platformHandler.getFileManagerConfig().storageQuota) return
    try {
        const res = await ApiService.axios.get(endpoints.storage_quota)
        const data = res.data?.data
        if (data) {
            state.storageQuota = data
        }
    } catch { /* empty */
    }
}

function _sortParam() {
    const map = {name: 'name', size: 'size', date: 'created_at', type: 'extension'}
    const field = map[state.sortBy] || 'name'
    return field + ':' + state.sortDir
}

function _fileLoadParams() {
    const folderId = state.currentFolderId
    if (!folderId) {
        const params = {unattached: true}
        if (platformHandler.platformType === 'admin') params.my_uploads = true
        return params
    }
    const folder = state.folders.find(f => f.id === folderId)
    if (!folder || folder.moduleType) return null
    if (folder.entityId) return {uploadable_type: folder.uploadableType, uploadable_id: folder.entityId}
    return {uploadable_type: 'FILE_MANAGER_FOLDER', uploadable_id: folder.id}
}

async function loadFiles(page) {
    const baseParams = _fileLoadParams()
    if (!baseParams) return
    state.fileLoading = true
    const limit = state._pageLimit
    try {
        const params = {...baseParams, page, page_limit: limit, sort: _sortParam()}
        if (state.searchQuery) params.name = state.searchQuery
        const res = await ApiService.axios.get(endpoints.upload, {params})
        const payload = res.data
        const items = payload?.data || []
        const folder = state.currentFolderId ? state.folders.find(f => f.id === state.currentFolderId) : null
        const mapped = items.map(f => {
            const m = mapApiFile(f)
            if (folder?.entityId) {
                m.module = folder.uploadableType
                m.moduleEntityId = folder.entityId
            }
            return m
        })

        if (page === 1) {
            state.files = mapped
        } else {
            state.files.push(...mapped)
        }

        const meta = payload?.meta
        if (meta) {
            state.fileHasMore = meta.has_more_page || (meta.current_page < meta.total_page)
            if (meta.total_record != null) {
                state.fileTotalCount = meta.total_record
            }
        } else {
            state.fileHasMore = mapped.length >= limit
        }
        state.filePage = page
        if (baseParams.unattached && page === 1) {
            state._unattachedCount = state.fileTotalCount ?? mapped.length
        }
    } catch { /* empty */
    }
    state.fileLoading = false
}

async function loadEntities(moduleType, parentFolderId, moduleIcon, page, searchQuery) {
    if (!platformHandler.getFileManagerConfig().entities) return
    state.entityLoading = true
    try {
        const params = {uploadable_type: moduleType, page, page_limit: 20}
        if (searchQuery) params.search = searchQuery
        const res = await ApiService.axios.get(endpoints.file_manager_entities, {params})
        const payload = res.data
        const items = payload?.data || []

        const entityFolders = items.map(e => ({
            id: 'entity_' + moduleType + '_' + e.id,
            uid: 'folder_entity_' + moduleType + '_' + e.id,
            name: e.label || ('#' + e.id),
            type: 'folder',
            parentId: parentFolderId,
            fileCount: e.file_count || 0,
            color: MODULE_COLORS[moduleType] || '#6B7280',
            createdAt: null,
            entityId: e.id,
            uploadableType: moduleType,
            directUpload: e.direct_upload ?? false,
            moduleIcon: moduleIcon,
        }))

        if (page === 1) {
            state.folders = state.folders.filter(f => f.parentId !== parentFolderId || !f.entityId)
        }
        state.folders.push(...entityFolders)

        if (state._pickerEntity && state._pickerEntity.uploadableType === moduleType) {
            const exists = state.folders.some(f => f.id === state._pickerEntity.id)
            if (!exists) state.folders.push(state._pickerEntity)
        }

        const meta = payload?.meta
        if (meta) {
            state.entityHasMore = meta.has_more_page || (meta.current_page < meta.total_page)
        } else {
            state.entityHasMore = items.length >= 20
        }
        state.entityPage = page
    } catch { /* empty */
    }
    state.entityLoading = false
}

async function loadMoreEntities() {
    if (!state.entityHasMore || state.entityLoading) return
    if (!state.entityParentId || !state.currentFolderId) return
    const folder = state.folders.find(f => f.id === state.entityParentId)
    if (!folder || !folder.moduleType) return
    await loadEntities(folder.moduleType, state.entityParentId, folder.moduleIcon, state.entityPage + 1, state.searchQuery)
}

async function loadMoreFiles() {
    if (!state.fileHasMore || state.fileLoading) return
    if (state.activeCategory === CATEGORIES.TRASH) {
        if (!state.trashFolderId) await loadTrash(state.filePage + 1)
        return
    }
    await loadFiles(state.filePage + 1)
}

function _restoreSettings() {
    if (state._settingsRestored) return
    state._settingsRestored = true
    const saved = panelManager.loadLayout('fileManagerPanel')
    if (saved.viewMode) state.viewMode = saved.viewMode
    if (saved.autoPreview != null) state.autoPreview = saved.autoPreview
    if (saved.dockBadge != null) state.dockBadge = saved.dockBadge
}

let _initPromise = null

async function init() {
    if (state._initialized) {
        _initPromise = refresh()
        return _initPromise
    }
    state._initialized = true
    _restoreSettings()
    state.loading = true

    _initPromise = Promise.all([
        apiInit(),
        loadStorageQuota(),
        loadTrashCount(),
    ])
    await _initPromise

    state.loading = false
    pushHistory()
}

async function loadTrashCount() {
    try {
        const res = await ApiService.axios.get(endpoints.file_manager_trash, {params: {page: 1, page_limit: 1}})
        const payload = res.data?.data
        const folderCount = payload?.folders?.length || 0
        const fileCount = payload?.files?.meta?.total_record ?? payload?.files?.data?.length ?? 0
        state._trashCount = folderCount + fileCount
    } catch { /* empty */ }
}

async function navigateToFolder(folderId) {
    if (state.activeCategory === CATEGORIES.TRASH) {
        const isTrashFolder = state.trashFolders.some(f => f.id === folderId)
        if (isTrashFolder) return navigateTrashFolder(folderId)
        state.activeCategory = CATEGORIES.ALL
        state.trashFolderId = null
        state.trashCurrentFolder = null
    }
    const navFolder = folderId ? state.folders.find(f => f.id === folderId) : null
    if (navFolder?.moduleType) {
        const cat = Object.keys(CATEGORIES).find(k => 'module_' + k === folderId) || null
        if (cat) state.activeCategory = CATEGORIES[cat]
    } else if (navFolder?.entityId) {
        const parentFolder = state.folders.find(f => f.id === navFolder.parentId)
        if (parentFolder?.moduleType) {
            const cat = Object.keys(CATEGORIES).find(k => CATEGORIES[k] === parentFolder.moduleType)
            if (cat) state.activeCategory = CATEGORIES[cat]
        }
    } else if (folderId) {
        state.activeCategory = CATEGORIES.ALL
    }
    state.currentFolderId = folderId
    state.selectedIds = []
    state.previewFile = null
    state.searchQuery = ''

    if (!folderId) {
        state.entityParentId = null
        state.loading = true
        await loadFiles(1)
        state.loading = false
        pushHistory()
        return
    }

    let folder = state.folders.find(f => f.id === folderId)
    if (!folder) {
        const moduleMatch = folderId.match(/^module_(.+)$/)
        if (moduleMatch) {
            const moduleType = moduleMatch[1]
            folder = {
                id: folderId,
                uid: 'folder_' + folderId,
                name: moduleType,
                nameKey: CATEGORY_LABELS[moduleType] || null,
                type: 'folder',
                parentId: null,
                fileCount: 0,
                color: MODULE_COLORS[moduleType] || '#6B7280',
                createdAt: null,
                moduleType,
                moduleIcon: MODULE_ICON_SVGS[moduleType] || null,
                directUpload: false,
            }
            state.folders.push(folder)
        } else {
            return
        }
    }

    if (folder.moduleType) {
        state.loading = true
        state.entityParentId = folderId
        try {
            await loadEntities(folder.moduleType, folderId, folder.moduleIcon, 1)
            state.files = []
        } catch { /* empty */
        }
        state.loading = false
    } else {
        state.loading = true
        await loadFiles(1)
        state.loading = false
    }
    pushHistory()
}

async function setCategory(category) {
    state.activeCategory = category
    state.currentFolderId = null
    state.selectedIds = []
    state.previewFile = null
    state.searchQuery = ''

    if (category === CATEGORIES.ALL) {
        state.loading = true
        await loadFiles(1)
        state.loading = false
        pushHistory()
    } else if (category.startsWith('MODULE_')) {
        const moduleFolderId = 'module_' + category
        let folder = state.folders.find(f => f.id === moduleFolderId)
        if (!folder) {
            folder = {
                id: moduleFolderId,
                uid: 'folder_' + moduleFolderId,
                name: category,
                nameKey: CATEGORY_LABELS[category] || null,
                type: 'folder',
                parentId: null,
                fileCount: 0,
                color: MODULE_COLORS[category] || '#6B7280',
                createdAt: null,
                moduleType: category,
                moduleIcon: MODULE_ICON_SVGS[category] || null,
                directUpload: false,
            }
            state.folders.push(folder)
        }
        state.currentFolderId = moduleFolderId
        await navigateToFolder(moduleFolderId)
    } else if (category === CATEGORIES.UNATTACHED) {
        state.loading = true
        await loadFiles(1)
        state.loading = false
        pushHistory()
    } else if (category === CATEGORIES.TRASH) {
        state.trashFolderId = null
        state.trashCurrentFolder = null
        state.loading = true
        await loadTrash(1)
        state.loading = false
        pushHistory()
    }
}

function setViewMode(mode) {
    state.viewMode = mode
}

function toggleAutoPreview() {
    state.autoPreview = !state.autoPreview
    if (!state.autoPreview) state.previewFile = null
}

function setSort(sortBy, sortDir) {
    if (state.sortBy === sortBy) {
        state.sortDir = state.sortDir === 'asc' ? 'desc' : 'asc'
    } else {
        state.sortBy = sortBy
        state.sortDir = sortDir || 'asc'
    }
    if (state.activeCategory === CATEGORIES.TRASH) {
        if (!state.trashFolderId) loadTrash(1)
        return
    }
    if (_fileLoadParams()) {
        loadFiles(1)
    }
}

let _searchTimer = null

function search(query) {
    state.searchQuery = query
    state.selectedIds = []
    state.searchLoading = true

    const folder = state.currentFolderId ? state.folders.find(f => f.id === state.currentFolderId) : null

    clearTimeout(_searchTimer)
    _searchTimer = setTimeout(async () => {
        if (folder?.moduleType) {
            await loadEntities(folder.moduleType, state.currentFolderId, folder.moduleIcon, 1, query)
        } else if (state.activeCategory === CATEGORIES.TRASH) {
            if (state.trashFolderId) await loadTrashFolder(state.trashFolderId)
            else await loadTrash(1)
        } else {
            await loadFiles(1)
        }
        state.searchLoading = false
    }, 300)
}

function toggleSelect(uid) {
    const idx = state.selectedIds.indexOf(uid)
    if (idx >= 0) {
        state.selectedIds.splice(idx, 1)
    } else {
        state.selectedIds.push(uid)
    }
}

function selectSingle(uid) {
    state.selectedIds = [uid]
}

function clearSelection() {
    state.selectedIds = []
}

function addToSelection(uid) {
    if (!state.selectedIds.includes(uid)) {
        state.selectedIds.push(uid)
    }
}

function selectRange(fromIdx, toIdx, allItems) {
    const start = Math.min(fromIdx, toIdx)
    const end = Math.max(fromIdx, toIdx)
    const uids = []
    for (let i = start; i <= end; i++) {
        const item = allItems[i]
        if (item && !item.moduleType && !item.entityId) {
            uids.push(item.uid)
        }
    }
    state.selectedIds = uids
}

function deduplicateSelection(uids) {
    const folderUids = new Set()
    const folderIds = new Map()
    uids.forEach(uid => {
        const folder = state.folders.find(f => f.uid === uid && !f.moduleType && !f.entityId)
        if (folder) {
            folderUids.add(uid)
            folderIds.set(uid, folder.id)
        }
    })

    const filtered = uids.filter(uid => {
        if (!folderUids.has(uid)) return true
        const folderId = folderIds.get(uid)
        for (const [otherUid, otherId] of folderIds) {
            if (otherUid !== uid && isDescendantOf(folderId, otherId)) {
                return false
            }
        }
        return true
    })
    return filtered
}

function selectAll() {
    const folders = currentFolders.value.filter(f => !f.moduleType && !f.entityId).map(f => f.uid)
    state.selectedIds = folders.concat(currentFiles.value.map(f => f.uid))
}

function createFolder(name, explicitParentId) {
    const title = name || 'Yeni Klasör'
    const parentId = explicitParentId !== undefined ? explicitParentId : state.currentFolderId
    const realParent = parentId ? state.folders.find(f => f.id === parentId) : null
    if (realParent && (realParent.moduleType || realParent.entityId)) return null

    const tempId = generateId()
    const folder = {
        id: tempId,
        uid: 'folder_' + tempId,
        name: title,
        type: 'folder',
        parentId: realParent ? realParent.id : null,
        fileCount: 0,
        color: '#6B7280',
        createdAt: new Date().toISOString(),
        _pendingCreate: true,
    }
    state.folders.push(folder)
    return folder
}

async function saveFolderToBackend(folder) {
    if (!folder || !folder._pendingCreate) return
    const tempId = folder.id
    const reactiveFolder = state.folders.find(f => f.id === tempId)
    if (!reactiveFolder) return
    const realParent = reactiveFolder.parentId ? state.folders.find(f => f.id === reactiveFolder.parentId) : null
    try {
        const body = {title: reactiveFolder.name, color: reactiveFolder.color}
        if (realParent && !realParent.moduleType && !realParent.entityId) body.parent_id = realParent.id
        const res = await ApiService.axios.post(endpoints.file_manager_folder, body)
        const created = res.data?.data
        if (created) {
            reactiveFolder.id = created.id
            reactiveFolder.uid = 'folder_' + created.id
            reactiveFolder.name = created.title || reactiveFolder.name
            reactiveFolder.color = created.color || reactiveFolder.color
        }
        delete reactiveFolder._pendingCreate
    } catch {
        state.folders = state.folders.filter(f => f.id !== tempId)
        state.lastError = 'common.fileManager.errorFolderCreate'
    }
}

async function renameItemByUid(uid, newName) {
    const file = state.files.find(f => f.uid === uid)
    if (file) {
        _renameFile(file, newName)
        return
    }
    const folder = state.folders.find(f => f.uid === uid)
    if (folder) {
        await _renameFolder(folder, newName)
    }
}

async function _renameFile(file, newName) {
    const oldName = file.name
    const ext = file.extension
    file.name = newName.includes('.') ? newName : newName + '.' + ext
    file.updatedAt = new Date().toISOString()
    try {
        await ApiService.axios.put(endpoints.upload + '/' + file.id, {name: file.name})
    } catch {
        file.name = oldName
        state.lastError = 'common.fileManager.errorRename'
    }
}

async function _renameFolder(folder, newName) {
    const oldName = folder.name
    folder.name = newName
    if (folder._pendingCreate) {
        const reactiveFolder = state.folders.find(f => f.id === folder.id)
        await saveFolderToBackend(reactiveFolder || folder)
    } else {
        try {
            await ApiService.axios.put(endpoints.file_manager_folder + '/' + folder.id, {title: newName})
        } catch {
            folder.name = oldName
            state.lastError = 'common.fileManager.errorRename'
        }
    }
}

async function deleteItems(uids) {
    const uidSet = new Set(deduplicateSelection(uids))
    const fileIds = state.files.filter(f => uidSet.has(f.uid)).map(f => f.id)
    const folderIds = state.folders.filter(f => uidSet.has(f.uid) && !f.moduleType && !f.entityId).map(f => f.id)

    const promises = []
    if (fileIds.length) promises.push(ApiService.axios.post(endpoints.upload_batch_delete, {ids: fileIds}))
    if (folderIds.length) promises.push(ApiService.axios.post(endpoints.file_manager_folder_batch_delete, {ids: folderIds}))

    try {
        await Promise.all(promises)
        const deletedFiles = state.files.filter(f => uidSet.has(f.uid))
        state.files = state.files.filter(f => !uidSet.has(f.uid))
        state.folders = state.folders.filter(f => !uidSet.has(f.uid))
        state.selectedIds = state.selectedIds.filter(uid => !uidSet.has(uid))
        if (state.previewFile && uidSet.has(state.previewFile.uid)) {
            state.previewFile = null
        }
        if (deletedFiles.length && state._pickerEvents?.onDelete) state._pickerEvents.onDelete(deletedFiles)
        await Promise.all([loadFiles(1), refreshFolderTree(), refreshCategories(), loadTrashCount()])
    } catch {
        state.lastError = 'common.fileManager.errorDelete'
    }
}

function mapTrashFolder(f, parentId) {
    return {
        id: f.id,
        uid: 'folder_' + f.id,
        name: f.title || f.name || ('Klasör #' + f.id),
        type: 'folder',
        parentId: parentId,
        color: f.color || '#6B7280',
        childrenCount: f.children_count || 0,
        fileCount: f.files_count || 0,
        deletedAt: f.deleted_at || null,
        createdAt: f.deleted_at || null,
    }
}

async function loadTrash(page) {
    if (page === 1) {
        state.files = []
        state.trashFolders = []
        state.fileTotalCount = null
        state.fileHasMore = false
    }
    state.fileLoading = true
    const limit = state._pageLimit
    try {
        const params = {page, page_limit: limit, sort: _sortParam()}
        if (state.searchQuery) params.search = state.searchQuery
        const res = await ApiService.axios.get(endpoints.file_manager_trash, {params})
        const payload = res.data?.data

        if (page === 1 && payload?.folders) {
            state.trashFolders = payload.folders.map(f => mapTrashFolder(f, null))
        }

        const filesData = payload?.files
        const items = filesData?.data || []
        const mapped = items.map(mapApiFile)

        if (page === 1) {
            state.files = mapped
        } else {
            state.files.push(...mapped)
        }

        const meta = filesData?.meta
        if (meta) {
            state.fileHasMore = meta.has_more_page || false
            if (meta.total_record != null) state.fileTotalCount = meta.total_record
        } else {
            state.fileHasMore = mapped.length >= limit
        }
        state.filePage = page
        if (page === 1) {
            state._trashCount = state.trashFolders.length + (state.fileTotalCount ?? state.files.length)
        }
    } catch { /* empty */
    }
    state.fileLoading = false
}

async function loadTrashFolder(folderId) {
    state.loading = true
    try {
        const res = await ApiService.axios.get(endpoints.file_manager_trash_folder + '/' + folderId)
        const payload = res.data?.data

        state.trashFolders = (payload?.children || []).map(f => mapTrashFolder(f, folderId))
        state.files = (payload?.files || []).map(mapApiFile)
        state.fileHasMore = false
        state.fileTotalCount = state.files.length
    } catch { /* empty */
    }
    state.loading = false
}

async function navigateTrashFolder(folderId) {
    state.selectedIds = []
    state.previewFile = null
    state.searchQuery = ''

    if (!folderId) {
        state.trashFolderId = null
        state.trashCurrentFolder = null
        await loadTrash(1)
        pushHistory()
        return
    }

    const folder = state.trashFolders.find(f => f.id === folderId)
    state.trashFolderId = folderId
    state.trashCurrentFolder = folder ? {id: folder.id, name: folder.name} : {id: folderId, nameKey: 'common.fileManager.folder'}
    await loadTrashFolder(folderId)
    pushHistory()
}

async function restoreItems(uids) {
    const uidSet = new Set(uids)
    const fileIds = state.files.filter(f => uidSet.has(f.uid)).map(f => f.id)
    const folderIds = state.trashFolders.filter(f => uidSet.has(f.uid)).map(f => f.id)

    const promises = []
    if (fileIds.length) promises.push(ApiService.axios.post(endpoints.file_manager_trash + '/file/batch/restore', {ids: fileIds}))
    if (folderIds.length) promises.push(ApiService.axios.post(endpoints.file_manager_trash + '/folder/batch/restore', {ids: folderIds}))

    try {
        await Promise.all(promises)
        state.files = state.files.filter(f => !uidSet.has(f.uid))
        state.trashFolders = state.trashFolders.filter(f => !uidSet.has(f.uid))
        state.selectedIds = state.selectedIds.filter(uid => !uidSet.has(uid))
        if (state.previewFile && uidSet.has(state.previewFile.uid)) {
            state.previewFile = null
        }
        await Promise.all([refreshFolderTree(), refreshCategories(), loadTrashCount()])
    } catch {
        state.lastError = 'common.fileManager.errorRestore'
    }
}

async function permanentDeleteItems(uids) {
    const uidSet = new Set(uids)
    const fileIds = state.files.filter(f => uidSet.has(f.uid)).map(f => f.id)
    const folderIds = state.trashFolders.filter(f => uidSet.has(f.uid)).map(f => f.id)

    const promises = []
    if (fileIds.length) promises.push(ApiService.axios.post(endpoints.file_manager_trash + '/file/batch/delete', {ids: fileIds}))
    if (folderIds.length) promises.push(ApiService.axios.post(endpoints.file_manager_trash + '/folder/batch/delete', {ids: folderIds}))

    try {
        await Promise.all(promises)
        state.files = state.files.filter(f => !uidSet.has(f.uid))
        state.trashFolders = state.trashFolders.filter(f => !uidSet.has(f.uid))
        state.selectedIds = state.selectedIds.filter(uid => !uidSet.has(uid))
        if (state.previewFile && uidSet.has(state.previewFile.uid)) {
            state.previewFile = null
        }
        await Promise.all([refreshCategories(), loadTrashCount(), loadStorageQuota()])
    } catch {
        state.lastError = 'common.fileManager.errorPermanentDelete'
    }
}

async function emptyTrash() {
    const fileIds = state.files.map(f => f.id)
    const folderIds = state.trashFolders.map(f => f.id)
    if (!fileIds.length && !folderIds.length) return

    const promises = []
    if (fileIds.length) promises.push(ApiService.axios.post(endpoints.file_manager_trash + '/file/batch/delete', {ids: fileIds}))
    if (folderIds.length) promises.push(ApiService.axios.post(endpoints.file_manager_trash + '/folder/batch/delete', {ids: folderIds}))

    try {
        await Promise.all(promises)
        state.files = []
        state.trashFolders = []
        state.selectedIds = []
        state.previewFile = null
        state._trashCount = 0
        await Promise.all([refreshCategories(), loadStorageQuota()])
    } catch {
        state.lastError = 'common.fileManager.errorPermanentDelete'
    }
}

async function restoreAllTrash() {
    const fileIds = state.files.map(f => f.id)
    const folderIds = state.trashFolders.map(f => f.id)
    if (!fileIds.length && !folderIds.length) return

    const promises = []
    if (fileIds.length) promises.push(ApiService.axios.post(endpoints.file_manager_trash + '/file/batch/restore', {ids: fileIds}))
    if (folderIds.length) promises.push(ApiService.axios.post(endpoints.file_manager_trash + '/folder/batch/restore', {ids: folderIds}))

    try {
        await Promise.all(promises)
        state.files = []
        state.trashFolders = []
        state.selectedIds = []
        state.previewFile = null
        state._trashCount = 0
        await Promise.all([refreshFolderTree(), refreshCategories()])
    } catch {
        state.lastError = 'common.fileManager.errorRestore'
    }
}

function setFolderColor(folderId, color) {
    const folder = state.folders.find(f => f.id === folderId)
    if (!folder) return
    const oldColor = folder.color
    folder.color = color
    ApiService.axios.put(endpoints.file_manager_folder + '/' + folderId, {color}).catch(() => {
        folder.color = oldColor
    })
}

async function reorderFolders(updates) {
    const oldValues = []
    const items = []
    updates.forEach(u => {
        const folder = state.folders.find(f => f.id === u.id)
        if (!folder) return
        oldValues.push({ id: u.id, sort: folder.sort, parentId: folder.parentId })
        folder.sort = u.sort
        if (u.parentChanged) folder.parentId = u.parent_id
        if (folder._pendingCreate) return
        const item = { id: u.id, sort: u.sort }
        if (u.parentChanged) item.parent_id = u.parent_id
        items.push(item)
    })
    try {
        await ApiService.axios.post(endpoints.file_manager_folder_batch, { items })
        await refreshFolderTree()
    } catch {
        oldValues.forEach(o => {
            const folder = state.folders.find(f => f.id === o.id)
            if (folder) {
                folder.sort = o.sort
                folder.parentId = o.parentId
            }
        })
        state.lastError = 'common.fileManager.errorMove'
    }
}

async function moveFolderToParent(folderId, parentId) {
    const folder = state.folders.find(f => f.id === folderId)
    if (!folder) return
    const oldParent = folder.parentId
    folder.parentId = parentId
    try {
        await ApiService.axios.put(endpoints.file_manager_folder + '/' + folderId, {parent_id: parentId})
        await refreshFolderTree()
    } catch {
        folder.parentId = oldParent
        state.lastError = 'common.fileManager.errorMove'
    }
}

async function refreshCategories() {
    if (!platformHandler.getFileManagerConfig().entities) return
    try {
        const res = await ApiService.axios.get(endpoints.file_manager_entities)
        const entities = res.data?.data || res.data || []
        const all = Array.isArray(entities) ? entities : []
        state.apiCategories = filterEntities(all, platformHandler.getFileManagerConfig())
    } catch { /* empty */ }
}

async function loadUnattachedCount() {
    try {
        const res = await ApiService.axios.get(endpoints.upload, {params: {unattached: true, page: 1, page_limit: 1}})
        const meta = res.data?.meta
        if (meta?.total_record != null) state._unattachedCount = meta.total_record
    } catch { /* empty */ }
}

async function refreshFolderTree() {
    try {
        const res = await ApiService.axios.get(endpoints.file_manager_folder_tree)
        const folderData = res.data?.data || res.data || []
        const apiFolders = Array.isArray(folderData) ? mapApiFolderTree(folderData, null) : []
        const moduleFolders = state.folders.filter(f => f.moduleType)
        state.folders = [...moduleFolders, ...apiFolders]
    } catch { /* empty */ }
}

async function moveItems(uids, targetFolderId) {
    const uidSet = new Set(deduplicateSelection(uids))
    const foldersToMove = state.folders.filter(f => uidSet.has(f.uid) && !f.moduleType && !f.entityId)
    const filesToMove = state.files.filter(f => uidSet.has(f.uid))

    const oldFolderParents = new Map()
    foldersToMove.forEach(f => { oldFolderParents.set(f.id, f.parentId); f.parentId = targetFolderId })
    const oldFileFolders = new Map()
    filesToMove.forEach(f => { oldFileFolders.set(f.id, f.folderId); f.folderId = targetFolderId })

    const promises = []
    if (foldersToMove.length) {
        const items = foldersToMove.map(f => ({id: f.id, parent_id: targetFolderId || null}))
        promises.push(ApiService.axios.post(endpoints.file_manager_folder_batch, {items}))
    }
    if (filesToMove.length) {
        const items = filesToMove.map(f => ({id: f.id, uploadable_id: targetFolderId || null}))
        promises.push(ApiService.axios.post(endpoints.file_manager_file_batch, {items}))
    }

    try {
        await Promise.all(promises)
    } catch {
        foldersToMove.forEach(f => { f.parentId = oldFolderParents.get(f.id) })
        filesToMove.forEach(f => { f.folderId = oldFileFolders.get(f.id) })
        state.lastError = 'common.fileManager.errorMove'
    }

    state.files = state.files.filter(f => !uidSet.has(f.uid))
    state.selectedIds = state.selectedIds.filter(uid => !uidSet.has(uid))
    if (state.previewFile && uidSet.has(state.previewFile.uid)) {
        state.previewFile = null
    }

    await Promise.all([loadFiles(1), refreshFolderTree()])
}

async function uploadFiles(fileList, opts) {
    const files = Array.from(fileList)
    if (!files.length) return

    let uploadableType = null
    let uploadableId = null

    if (opts?.module && opts?.moduleEntityId) {
        uploadableType = opts.module
        uploadableId = opts.moduleEntityId
    } else if (state.currentFolderId) {
        const folder = state.folders.find(f => f.id === state.currentFolderId)
        if (folder?.entityId) {
            uploadableType = folder.uploadableType
            uploadableId = folder.entityId
        } else if (folder && !folder.moduleType) {
            uploadableType = 'FILE_MANAGER_FOLDER'
            uploadableId = folder.id
        }
    }

    const formData = new FormData()
    files.forEach(f => formData.append('files[]', f))
    if (uploadableType) formData.append('uploadable_type', uploadableType)
    if (uploadableId) formData.append('uploadable_id', uploadableId)

    const totalBytes = files.reduce((s, f) => s + (f.size || 0), 0)
    let uploadItem = {
        id: generateId(),
        files: files.map(f => ({name: f.name, size: f.size})),
        progress: 0,
        status: 'uploading',
        totalBytes,
        loadedBytes: 0,
        speed: 0,
        eta: 0,
        startTime: Date.now(),
    }
    state.uploadQueue.push(uploadItem)
    uploadItem = state.uploadQueue[state.uploadQueue.length - 1]

    try {
        const res = await ApiService.axios.post(endpoints.upload, formData, {
            headers: {'Content-Type': 'multipart/form-data'},
            onUploadProgress(e) {
                const loaded = e.loaded || 0
                uploadItem.loadedBytes = loaded
                uploadItem.progress = totalBytes > 0 ? Math.round((loaded / totalBytes) * 100) : 0
                const elapsed = (Date.now() - uploadItem.startTime) / 1000
                uploadItem.speed = elapsed > 0 ? loaded / elapsed : 0
                const remaining = totalBytes - loaded
                uploadItem.eta = uploadItem.speed > 0 ? remaining / uploadItem.speed : 0
            },
        })

        uploadItem.progress = 100
        uploadItem.loadedBytes = totalBytes
        uploadItem.status = 'done'
        uploadItem.eta = 0

        const uploaded = res.data?.data || []
        const mappedUploaded = Array.isArray(uploaded) ? uploaded.map(mapApiFile) : []
        if (mappedUploaded.length) {
            state.files.push(...mappedUploaded)
            if (!uploadableType) state._unattachedCount += mappedUploaded.length
            if (state._pickerEvents?.onUpload) state._pickerEvents.onUpload(mappedUploaded)
        }

        loadStorageQuota()
        refreshCategories()
        if (uploadableType === 'FILE_MANAGER_FOLDER') refreshFolderTree()

        setTimeout(() => {
            const idx = state.uploadQueue.indexOf(uploadItem)
            if (idx >= 0) state.uploadQueue.splice(idx, 1)
        }, 1500)
    } catch (err) {
        uploadItem.status = 'error'
        const msg = err?.response?.data?.message
        if (msg === 'storage_quota_not_found') state.lastError = 'common.storage.quotaNotFound'
        else if (msg === 'storage_quota_exceeded') state.lastError = 'common.storage.quotaExceeded'
        else state.lastError = 'common.fileManager.errorUpload'
        setTimeout(() => {
            const idx = state.uploadQueue.indexOf(uploadItem)
            if (idx >= 0) state.uploadQueue.splice(idx, 1)
        }, 3000)
    }
}

async function refresh() {
    if (state.activeCategory === CATEGORIES.TRASH) {
        state.loading = true
        if (state.trashFolderId) {
            await loadTrashFolder(state.trashFolderId)
        } else {
            await loadTrash(1)
        }
        state.loading = false
        return
    }

    if (!state.currentFolderId) {
        state.loading = true
        await apiInit()
        state.loading = false
        return
    }

    const folder = state.folders.find(f => f.id === state.currentFolderId)
    if (!folder) return

    if (folder.moduleType) {
        state.entityParentId = folder.id
        state.loading = true
        await loadEntities(folder.moduleType, folder.id, folder.moduleIcon, 1)
        state.files = []
        state.loading = false
    } else {
        state.loading = true
        await loadFiles(1)
        state.loading = false
    }
}

function getModuleEntityIds(module) {
    const ids = new Set()
    state.files.forEach(f => {
        if (f.module === module && f.moduleEntityId) ids.add(f.moduleEntityId)
    })
    return Array.from(ids)
}

function setPreviewFile(file) {
    state.previewFile = file
}

async function fetchFileById(id) {
    try {
        const res = await ApiService.axios.get(endpoints.upload + '/' + id)
        const f = res.data?.data || res.data
        if (f) return mapApiFile(f)
    } catch { /* empty */ }
    return null
}

const currentFolders = computed(() => {
    if (state.activeCategory === CATEGORIES.UNATTACHED) return []

    if (state.activeCategory === CATEGORIES.TRASH) {
        let folders = state.trashFolders
        if (state.searchQuery) {
            const q = state.searchQuery.toLowerCase()
            folders = folders.filter(f => f.name.toLowerCase().includes(q))
        }
        return folders
    }

    const all = state.folders.filter(f => f.parentId === state.currentFolderId)
    const virtual = all.filter(f => f.moduleType || f.entityId)
        .sort((a, b) => (CATEGORY_ORDER[a.moduleType] ?? 99) - (CATEGORY_ORDER[b.moduleType] ?? 99))
    let regular = all.filter(f => !f.moduleType && !f.entityId)

    if (state.searchQuery) {
        const q = state.searchQuery.toLowerCase()
        regular = regular.filter(f => f.name.toLowerCase().includes(q))
    }

    return [...virtual, ...sortItems(regular)]
})

const currentFiles = computed(() => {
    return state.files
})

function sortItems(items) {
    const dir = state.sortDir === 'asc' ? 1 : -1
    return [...items].sort((a, b) => {
        switch (state.sortBy) {
            case 'name':
                return dir * a.name.localeCompare(b.name, 'tr')
            case 'size':
                return dir * ((a.size || 0) - (b.size || 0))
            case 'date':
                return dir * (new Date(a.updatedAt || a.createdAt) - new Date(b.updatedAt || b.createdAt))
            case 'type':
                return dir * ((a.extension || '').localeCompare(b.extension || ''))
            default:
                return 0
        }
    })
}

const breadcrumbs = computed(() => {
    if (state.activeCategory === CATEGORIES.TRASH) {
        const crumbs = [{id: null, nameKey: 'common.fileManager.trash'}]
        if (state.trashCurrentFolder) {
            crumbs.push({id: state.trashCurrentFolder.id, name: state.trashCurrentFolder.name})
        }
        return crumbs
    }

    const rootKey = CATEGORY_LABELS[state.activeCategory] || 'common.fileManager.allFiles'
    const moduleFolderId = 'module_' + state.activeCategory
    const moduleFolder = state.folders.find(f => f.id === moduleFolderId && f.moduleType)
    const crumbs = [{id: moduleFolder ? moduleFolderId : null, nameKey: rootKey}]
    if (!state.currentFolderId) return crumbs

    const buildPath = (folderId) => {
        const folder = state.folders.find(f => f.id === folderId)
        if (!folder) return
        if (folder.moduleType) return
        if (folder.parentId) buildPath(folder.parentId)
        crumbs.push({id: folder.id, name: folder.name, nameKey: folder.nameKey || null})
    }

    buildPath(state.currentFolderId)
    return crumbs
})

const folderTree = computed(() => {
    const build = (parentId) => {
        return state.folders
            .filter(f => f.parentId === parentId && !f.moduleType && !f.entityId)
            .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
            .map(f => ({
                ...f,
                children: build(f.id),
            }))
    }
    return build(null)
})

const storageStats = computed(() => {
    const files = currentFiles.value
    const folders = currentFolders.value
    const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0)
    return {
        totalFiles: state.fileTotalCount != null ? state.fileTotalCount : files.length,
        totalFolders: folders.filter(f => !f.moduleType && !f.entityId).length,
        totalSize,
        totalSizeFormatted: formatFileSize(totalSize),
    }
})

const categoryCounts = computed(() => {
    const counts = {}
    let total = 0
    state.apiCategories.forEach(c => {
        if (c.type !== 'FILE_MANAGER_FOLDER') total += c.file_count || 0
    })
    let folderFilesTotal = 0
    state.folders.forEach(f => {
        if (!f.moduleType && !f.entityId) folderFilesTotal += f.fileCount || 0
    })
    counts[CATEGORIES.ALL] = total + state._unattachedCount + folderFilesTotal
    state.apiCategories.forEach(c => {
        if (c.type !== 'FILE_MANAGER_FOLDER' && CATEGORIES[c.type]) {
            counts[CATEGORIES[c.type]] = c.file_count || 0
        }
    })
    counts[CATEGORIES.UNATTACHED] = state._unattachedCount
    return counts
})

function toggleDockBadge() {
    state.dockBadge = !state.dockBadge
    const saved = panelManager.loadLayout('fileManagerPanel')
    panelManager.saveLayout('fileManagerPanel', {...saved, dockBadge: state.dockBadge})
}

function updatePageLimit(height, width, viewMode) {
    let count
    if (viewMode === 'list') {
        count = Math.ceil(height / 34)
    } else {
        const cols = Math.max(1, Math.floor(width / 118))
        const rows = Math.ceil(height / 110)
        count = cols * rows
    }
    state._pageLimit = count + 10
}

const CATEGORY_ORDER = {
    ALL: 0,
    MODULE_NOTE: 1,
    MODULE_VEHICLE_WORK_ORDER: 2,
    MODULE_MUSIC: 3,
    MODULE_MUSIC_PLAYLIST: 4,
    UNATTACHED: 5,
    TRASH: 6,
}

const sidebarCategories = computed(() => {
    const fmConfig = platformHandler.getFileManagerConfig()
    const cats = [CATEGORIES.ALL]
    if (fmConfig.entities) {
        const entityCats = []
        for (const cat of state.apiCategories) {
            if (cat.type !== 'FILE_MANAGER_FOLDER' && CATEGORIES[cat.type]) {
                entityCats.push(CATEGORIES[cat.type])
            }
        }
        entityCats.sort((a, b) => (CATEGORY_ORDER[a] ?? 99) - (CATEGORY_ORDER[b] ?? 99))
        cats.push(...entityCats)
    }
    cats.push(CATEGORIES.UNATTACHED)
    return cats
})

const isTrashMode = computed(() => state.activeCategory === CATEGORIES.TRASH)

async function navigateToEntityDirect(uploadableType, uploadableId) {
    const earlyCat = Object.keys(CATEGORIES).find(k => CATEGORIES[k] === uploadableType)
    if (earlyCat) state.activeCategory = CATEGORIES[earlyCat]
    if (_initPromise) await _initPromise
    state.loading = true

    const moduleFolderId = 'module_' + uploadableType
    const entityFolderId = 'entity_' + uploadableType + '_' + uploadableId

    if (state.activeCategory === CATEGORIES.TRASH) {
        state.activeCategory = CATEGORIES.ALL
        state.trashFolderId = null
        state.trashCurrentFolder = null
    }

    const existing = state.folders.find(f => f.id === entityFolderId)
    if (existing) {
        const cat = Object.keys(CATEGORIES).find(k => CATEGORIES[k] === uploadableType)
        if (cat) state.activeCategory = CATEGORIES[cat]
        state.currentFolderId = entityFolderId
        state.selectedIds = []
        state.previewFile = null
        state.searchQuery = ''
        await loadFiles(1)
        state.loading = false
        pushHistory()
        return
    }

    let label = '#' + uploadableId
    let directUpload = true
    let fileCount = 0
    try {
        const res = await ApiService.axios.get(endpoints.file_manager_entity_check, {
            params: { uploadable_type: uploadableType, uploadable_id: uploadableId }
        })
        const entity = res.data?.data
        if (entity) {
            label = entity.label || label
            directUpload = entity.direct_upload ?? true
            fileCount = entity.file_count ?? 0
        }
    } catch { /* empty */ }

    let moduleFolder = state.folders.find(f => f.id === moduleFolderId)
    if (!moduleFolder) {
        moduleFolder = {
            id: moduleFolderId,
            uid: 'folder_' + moduleFolderId,
            name: uploadableType,
            nameKey: CATEGORY_LABELS[uploadableType] || null,
            type: 'folder',
            parentId: null,
            fileCount: 0,
            color: MODULE_COLORS[uploadableType] || '#6B7280',
            createdAt: null,
            moduleType: uploadableType,
            moduleIcon: MODULE_ICON_SVGS[uploadableType] || null,
            directUpload: false,
        }
        state.folders.push(moduleFolder)
    }
    const syntheticFolder = {
        id: entityFolderId,
        uid: 'folder_' + entityFolderId,
        name: label,
        type: 'folder',
        parentId: moduleFolderId,
        fileCount,
        color: MODULE_COLORS[uploadableType] || '#6B7280',
        createdAt: null,
        entityId: uploadableId,
        uploadableType,
        directUpload,
        moduleIcon: moduleFolder?.moduleIcon || MODULE_ICON_SVGS[uploadableType] || null,
    }
    state.folders.push(syntheticFolder)

    const cat = Object.keys(CATEGORIES).find(k => CATEGORIES[k] === uploadableType)
    if (cat) state.activeCategory = CATEGORIES[cat]
    state.currentFolderId = entityFolderId
    state.selectedIds = []
    state.previewFile = null
    state.searchQuery = ''
    await loadFiles(1)
    state.loading = false
    pushHistory()
}

const MIME_MAP = {
    jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', webp: 'image', svg: 'image', bmp: 'image', ico: 'image',
    mp4: 'video', mov: 'video', avi: 'video', webm: 'video', mkv: 'video',
    mp3: 'audio', wav: 'audio', ogg: 'audio', flac: 'audio', aac: 'audio',
    pdf: 'application/pdf',
    doc: 'application/msword', docx: 'application/msword',
    xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.ms-excel',
    zip: 'application/zip', rar: 'application/zip', '7z': 'application/zip',
}

function matchesAccept(file, acceptPattern) {
    if (!acceptPattern) return true
    if (file.type === 'folder') return true

    const ext = (file.extension || file.name?.split('.').pop() || '').toLowerCase()
    const patterns = acceptPattern.split(',').map(p => p.trim()).filter(Boolean)

    for (const pattern of patterns) {
        if (pattern.startsWith('.')) {
            if (ext === pattern.slice(1).toLowerCase()) return true
        } else if (pattern.endsWith('/*')) {
            const category = pattern.split('/')[0]
            const fileMime = MIME_MAP[ext]
            if (fileMime === category || (fileMime && fileMime.startsWith(category + '/'))) return true
        } else if (pattern === '*/*') {
            return true
        }
    }
    return false
}

export default {
    state,
    CATEGORIES,
    CATEGORY_LABELS,
    CATEGORY_ICONS,
    MODULE_ICON_SVGS,
    MODULE_COLORS,
    FILE_ICONS,
    getFileIcon,
    getFileIconSvg,
    formatFileSize,
    formatDate,
    currentFolders,
    currentFiles,
    breadcrumbs,
    folderTree,
    storageStats,
    categoryCounts,
    isTrashMode,
    sidebarCategories,
    canGoBack,
    canGoForward,
    goBack,
    goForward,
    init,
    navigateToFolder,
    setCategory,
    setViewMode,
    toggleAutoPreview,
    setSort,
    search,
    toggleSelect,
    selectSingle,
    addToSelection,
    selectRange,
    deduplicateSelection,
    clearSelection,
    selectAll,
    createFolder,
    saveFolderToBackend,
    renameItemByUid,
    deleteItems,
    moveItems,
    reorderFolders,
    moveFolderToParent,
    refreshFolderTree,
    setPreviewFile,
    generateId,
    getModuleEntityIds,
    setFolderColor,
    toggleDockBadge,
    loadMoreFiles,
    loadMoreEntities,
    uploadFiles,
    updatePageLimit,
    refresh,
    restoreItems,
    permanentDeleteItems,
    emptyTrash,
    restoreAllTrash,
    ENTITY_NAV,
    resolveEntityLabel,
    navigateEntity,
    MAX_FOLDER_DEPTH,
    canCreateSubfolder,
    isDescendantOf,
    fetchFileById,
    navigateToEntityDirect,
    matchesAccept,
    refreshCategories,
    loadUnattachedCount,
    restoreSettings: _restoreSettings,
    mapApiFile,
}
